import { useState, useEffect, useCallback, useMemo } from 'react'
import { RotateCcw, ChevronDown, ChevronRight, History } from 'lucide-react'
import type { AgentConfigListItem, AgentConfigUpdate } from '../../lib/api'
import { getEffectiveAgentConfig } from '../../lib/api'
import { useAvailableModels, usePromptHistory } from '../../hooks/useAIConfig'
import { ModelSelector } from './ModelSelector'
import { LoadingSpinner } from '../ui/LoadingSpinner'

interface AgentConfigFormProps {
  agent: AgentConfigListItem
  onSave: (agentKey: string, data: AgentConfigUpdate) => Promise<void>
  onReset: (agentKey: string) => Promise<void>
  onRestorePromptVersion: (agentKey: string, promptId: string) => Promise<void>
  isSaving: boolean
  isResetting: boolean
  isRestoringPrompt: boolean
}

const PREVIEW_LEN = 80

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    })
  } catch {
    return s
  }
}

export function AgentConfigForm({
  agent,
  onSave,
  onReset,
  onRestorePromptVersion,
  isSaving,
  isResetting,
  isRestoringPrompt,
}: AgentConfigFormProps) {
  const { data: models = [] } = useAvailableModels()
  const effective = useMemo(() => getEffectiveAgentConfig(agent), [agent])
  const promptHistory = usePromptHistory(agent.agent_id)

  const [systemPrompt, setSystemPrompt] = useState(effective.system_prompt ?? '')
  const [modelOverride, setModelOverride] = useState<string | null>(effective.model_override)
  const [temperature, setTemperature] = useState(effective.temperature)
  const [maxTokens, setMaxTokens] = useState(effective.max_tokens)
  const [timeoutSeconds, setTimeoutSeconds] = useState(effective.timeout_seconds)
  const [maxRetries, setMaxRetries] = useState(effective.max_retries)
  const [isActive, setIsActive] = useState(effective.is_active)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [showDefaultPrompt, setShowDefaultPrompt] = useState(false)

  const syncFromAgent = useCallback((a: AgentConfigListItem) => {
    const eff = getEffectiveAgentConfig(a)
    setSystemPrompt(eff.system_prompt ?? '')
    setModelOverride(eff.model_override)
    setTemperature(eff.temperature)
    setMaxTokens(eff.max_tokens)
    setTimeoutSeconds(eff.timeout_seconds)
    setMaxRetries(eff.max_retries)
    setIsActive(eff.is_active)
    setShowResetConfirm(false)
  }, [])

  useEffect(() => {
    syncFromAgent(agent)
  }, [agent, syncFromAgent])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSave(agent.agent_id, {
      system_prompt: systemPrompt || null,
      model_override: modelOverride,
      temperature,
      max_tokens: maxTokens,
      max_retries: maxRetries,
      timeout_seconds: timeoutSeconds,
      is_active: isActive,
    })
  }

  const handleReset = async () => {
    setShowResetConfirm(false)
    await onReset(agent.agent_id)
  }

  const loadDefaultPrompt = () => {
    const d = agent.default?.system_prompt ?? ''
    setSystemPrompt(d)
  }

  const defaultSystemPrompt = agent.default?.system_prompt ?? null
  const hasPromptType = !!defaultSystemPrompt

  const tempPercent = (temperature / 2) * 100
  const tempColor =
    temperature <= 0.5
      ? 'from-blue-500 to-blue-400'
      : temperature <= 1.0
        ? 'from-blue-400 to-yellow-400'
        : temperature <= 1.5
          ? 'from-yellow-400 to-orange-500'
          : 'from-orange-500 to-red-500'

  const busy = isSaving || isResetting || isRestoringPrompt
  const historyItems = promptHistory.data ?? []

  return (
    <form onSubmit={handleSave} className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">{agent.display_name}</h2>
          <span className="text-xs font-mono text-slate-400 mt-1 block">{agent.agent_id}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-slate-600">{isActive ? 'Activo' : 'Inactivo'}</span>
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              onClick={() => setIsActive(!isActive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isActive ? 'bg-emerald-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        </div>
      </div>

      <div>
        <label className="label">System Prompt</label>
        <textarea
          className="input font-mono text-sm"
          rows={8}
          style={{ resize: 'vertical', minHeight: '160px' }}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder="(default del sistema)"
          disabled={busy}
        />
        <p className="text-xs text-slate-400 mt-1 text-right">{systemPrompt.length} caracteres</p>

        {hasPromptType && (
          <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowDefaultPrompt((v) => !v)}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 bg-white border-0"
            >
              {showDefaultPrompt ? (
                <ChevronDown className="w-4 h-4 shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 shrink-0" />
              )}
              <span>Ver default del sistema</span>
            </button>
            {showDefaultPrompt && (
              <div className="px-3 py-2 bg-slate-50 border-t border-slate-200">
                <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                  {defaultSystemPrompt}
                </pre>
                <button
                  type="button"
                  onClick={loadDefaultPrompt}
                  disabled={busy}
                  className="mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Cargar default (sin guardar)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <label className="label">Modelo</label>
        <ModelSelector
          value={modelOverride}
          onChange={setModelOverride}
          models={models}
          disabled={busy}
        />
        <p className="text-xs text-slate-400 mt-1">Dejar en "Automatico" para que el Router elija el modelo optimo</p>
      </div>

      <div>
        <label className="label">Temperatura: {temperature.toFixed(2)}</label>
        <div className="relative mt-1">
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-full rounded-full bg-gradient-to-r ${tempColor} transition-all`}
              style={{ width: `${tempPercent}%` }}
            />
          </div>
          <input
            type="range"
            min={0}
            max={2}
            step={0.05}
            value={temperature}
            onChange={(e) => setTemperature(parseFloat(e.target.value))}
            disabled={busy}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>0.0 (preciso)</span>
          <span>2.0 (creativo)</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="label">Max Tokens</label>
          <input
            type="number"
            className="input"
            min={256}
            max={32768}
            value={maxTokens}
            onChange={(e) => setMaxTokens(Number(e.target.value))}
            disabled={busy}
          />
        </div>
        <div>
          <label className="label">Timeout (s)</label>
          <input
            type="number"
            className="input"
            min={30}
            max={600}
            value={timeoutSeconds}
            onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
            disabled={busy}
          />
        </div>
        <div>
          <label className="label">Max Reintentos</label>
          <input
            type="number"
            className="input"
            min={0}
            max={10}
            value={maxRetries}
            onChange={(e) => setMaxRetries(Number(e.target.value))}
            disabled={busy}
          />
        </div>
      </div>

      {hasPromptType && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
            <History className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Historial de versiones</span>
          </div>
          <div className="p-3">
            {promptHistory.isLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : historyItems.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">Aun no hay versiones guardadas. Guarda el prompt para crear la primera.</p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto">
                {historyItems.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-3 py-2 px-3 rounded-md bg-slate-50 border border-slate-100"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-slate-700">v{item.version}</span>
                      <span className="text-xs text-slate-400 ml-2">{formatDate(item.created_at)}</span>
                      {item.is_active && (
                        <span className="ml-2 text-xs font-medium text-emerald-600">activa</span>
                      )}
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {item.system_prompt.slice(0, PREVIEW_LEN)}
                        {item.system_prompt.length > PREVIEW_LEN ? '...' : ''}
                      </p>
                    </div>
                    {!item.is_active && (
                      <button
                        type="button"
                        onClick={() => onRestorePromptVersion(agent.agent_id, item.id)}
                        disabled={busy}
                        className="btn text-sm px-2 py-1 shrink-0"
                      >
                        Restaurar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
        <button type="submit" disabled={busy} className="btn btn-primary">
          {isSaving ? 'Guardando...' : 'Guardar Cambios'}
        </button>

        {showResetConfirm ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Restaurar defaults?</span>
            <button
              type="button"
              onClick={handleReset}
              disabled={busy}
              className="btn text-sm px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
            >
              Confirmar
            </button>
            <button
              type="button"
              onClick={() => setShowResetConfirm(false)}
              className="btn text-sm px-3 py-1.5 bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowResetConfirm(true)}
            disabled={busy}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Restaurar Default
          </button>
        )}
      </div>
    </form>
  )
}
